import { useEffect, useState } from "react";
import { UnauthorizedViewerRequestError } from "../services/adminApiClient";
import { fetchViewerAuthoringStatus } from "../Viewer/authoring/api/viewerAuthoringApi";
import type { ViewerProjectStatus } from "../types/viewerDeployment";

interface UseViewerProjectStateParams {
  enabled: boolean;
  companyId: string | null;
  productId: string | null;
}

interface UseViewerProjectStateResult {
  projectStatus: ViewerProjectStatus | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  refetch: () => Promise<void>;
}

export function useViewerProjectState(
  params: UseViewerProjectStateParams,
): UseViewerProjectStateResult {
  const { enabled, companyId, productId } = params;
  const [projectStatus, setProjectStatus] = useState<ViewerProjectStatus | null>(
    null,
  );
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    enabled ? "loading" : "idle",
  );
  const [error, setError] = useState<string | null>(null);

  const refetch = async () => {
    if (!enabled || !companyId || !productId) {
      setProjectStatus(null);
      setStatus("idle");
      setError(null);
      return;
    }

    setStatus("loading");
    setError(null);

    try {
      const nextProjectStatus = await fetchViewerAuthoringStatus(
        companyId,
        productId,
      );
      setProjectStatus(nextProjectStatus);
      setStatus("ready");
    } catch (nextError) {
      setProjectStatus(null);
      setStatus("error");
      setError(
        nextError instanceof UnauthorizedViewerRequestError
          ? "Session expired. Please sign in again."
          : nextError instanceof Error
            ? nextError.message
            : "Failed to load project status.",
      );
    }
  };

  useEffect(() => {
    void refetch();
  }, [enabled, companyId, productId]);

  return { projectStatus, status, error, refetch };
}
